import type { ComponentProps, ReactElement } from "react";
import { useMemo, useRef } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import type { IOverlayHookProps } from "app/pages/sales-intelligence/common-components/overlay-hook";
import { FloatOverlayHook } from "app/pages/sales-intelligence/common-components/overlay-hook";
import { useGetTrialTableHookProps } from "app/pages/sales-intelligence/sub-modules/account-overview/competitive-landscape/hooks/useGetTrialTableHookProps";

import { DATA_TABLE_DEFAULT_PAGE } from "../../constants/default";
import getTrialMockedRows from "../../helpers/getTrialMockedRows";
import { SearchResultsTable } from "../SearchResultsTable/SearchResultsTable";

export type SearchResultsTrialTableProps<T extends object> = ComponentProps<typeof SearchResultsTable<T>> & {
    trialHookProps: Omit<IOverlayHookProps, "height" | "variant">;
    rowHeight: number;
    trialAvailableRows: number;
};

export const SearchResultsTrialTable = <T extends object>({
    trialHookProps,
    rowHeight,
    trialAvailableRows,
    ...tableProps
}: SearchResultsTrialTableProps<T>): ReactElement => {
    const mockedRef = useRef<readonly T[]>([]);

    const { components, isLoading, data, pagination } = tableProps;

    const isWithData = data && data.length > 0;
    const isShowHook = isWithData && !isLoading && data.length > trialAvailableRows;

    const { overlayHookProps, rowsCount: mockedRowsCount } = useGetTrialTableHookProps(rowHeight);

    const dataWithTrialMocks = useMemo(() => {
        const trialAvailableData = data.slice(0, trialAvailableRows);

        if (mockedRef.current.length !== mockedRowsCount) {
            mockedRef.current = getTrialMockedRows<T>(data, mockedRowsCount);
        }

        return [...trialAvailableData, ...mockedRef.current];
    }, [data, trialAvailableRows, mockedRowsCount]);

    const customPaginationProps = useMemo(() => {
        if (!pagination) {
            return pagination;
        }

        return {
            ...pagination,
            pageSize: dataWithTrialMocks.length,
            current: DATA_TABLE_DEFAULT_PAGE,
            total: dataWithTrialMocks.length,
        };
    }, [pagination, dataWithTrialMocks.length]);

    const customComponentsProps = useMemo(
        (): ComponentProps<typeof SearchResultsTable<T>>["components"] => ({
            ...(components || {}),
            table: (tableProps) => (
                <>
                    <table {...tableProps}>{tableProps.children}</table>
                    <Flex
                        position="absolute"
                        left={0}
                        width="100%"
                    >
                        <FloatOverlayHook
                            {...trialHookProps}
                            {...overlayHookProps}
                        />
                    </Flex>
                </>
            ),
        }),
        [components, trialHookProps, overlayHookProps],
    );

    const customizedProps = useMemo((): Partial<ComponentProps<typeof SearchResultsTable<T>>> => {
        if (!isShowHook) {
            return {};
        }

        return {
            data: dataWithTrialMocks,
            pagination: customPaginationProps,
            components: customComponentsProps,
        };
    }, [isShowHook, dataWithTrialMocks, customComponentsProps, customPaginationProps]);

    return (
        <SearchResultsTable<T>
            {...tableProps}
            {...customizedProps}
        />
    );
};
